import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import { PlusCircle, Save2, Trash, Calendar3, Receipt, Coin, Diagram3, Copy } from 'react-bootstrap-icons'
import { Container, Row, Col } from 'react-bootstrap'
import SearchSelect from '../components/form/SearchSelect'
import InputNumber from '../components/form/InputNumber'
import InputDate from '../components/form/InputDate'
import IncomesService from '../services/IncomesService'
import ProjectsService from '../services/ProjectsService'
import { AlertMsg, AlertError, AlertLoading } from '../helpers/AlertHelper'
import { formatDate } from "../helpers/momentHelper"

const IncomesForm = (props) => {

    const intl = useIntl()
    const [elementId, setElementId] = useState(props.elementId)
    const [action, setAction] = useState(props.action)
    const [projectId, setProjectId] = useState('')
    const [projectsList, setProjectsList] = useState([])
    const [projectErrorKey, setProjectErrorKey] = useState('')
    const [concept, setConcept] = useState('')
    const [amount, setAmount] = useState()
    const [estimatedDate, setEstimatedDate] = useState(formatDate(Date.now(), 'YYYY-MM-DD'))
    const [date, setDate] = useState(formatDate(Date.now(), 'YYYY-MM-DD'))
    const [buttonTitle, setButtonTitle] = useState('')
    const [inputDisabled, setInputDisabled] = useState(false)
    const [formValid, setFormValid] = useState(false)
    const [showMsg, setShowMsg] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(false)

    // Fetch projects
    const fetchProjects = async () => {
        try {
            const response = await ProjectsService.getProjects(1, '', 2000)
            setProjectsList(response.data.projects)
        } catch (err) {
            setError(err)
        }
    }

    const getData = async () => {
        await IncomesService.getIncome(elementId).then(
            response => {
                setProjectId(response.data.projectId || '')
                setConcept(response.data.concept || '')
                setAmount(response.data.amount || 0)
                setEstimatedDate(formatDate(response.data.estimatedDate, 'YYYY-MM-DD'))
                setDate(formatDate(response.data.date, 'YYYY-MM-DD'))
                setIsLoaded(true)
            },
            error => {
                setError(error)
            })
    }

    useEffect(() => {
        fetchProjects()

        if (elementId) {
            getData()
        }

        switch (action) {
            case 'edit':
                setButtonTitle(<FormattedMessage id="textSave" defaultMessage="Save" />)
                break
            case 'delete':
                setButtonTitle(<FormattedMessage id="textDelete" defaultMessage="Delete" />)
                setInputDisabled(true)
                break
            case 'duplicate':
                setButtonTitle(<FormattedMessage id="textDuplicate" defaultMessage="Duplicate" />)
                break
            default:
                setButtonTitle(<FormattedMessage id="textAdd" defaultMessage="Add" />)
        }

    }, [])

    const onSubmit = () => {
        props.onSubmit()
    }

    const handleInputChange = (e) => {
        const name = e.target.name
        const value = e.target.value
        if (name === 'projectId') {
            setProjectErrorKey('')
        }
        setFieldValue(name, value)
    }

    const setFieldValue = (fieldName, value) => {
        switch (fieldName) {
            case 'projectId':
                setProjectId(value)
                break
            case 'concept':
                setConcept(value)
                break
            case 'amount':
                setAmount(value)
                break
            case 'estimatedDate':
                setEstimatedDate(value)
                break
            case 'date':
                setDate(value)
                break
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()
        setProjectErrorKey('')

        const form = e.currentTarget
        let finalProjectId = projectId

        const matchedProject = projectsList.find(p => 
            p.name.toLowerCase() === (projectId?.toString().toLowerCase() || '') || 
            p._id === projectId
        );

        if (matchedProject) {
            finalProjectId = matchedProject._id
        }

        const hasValue = projectId && projectId.toString().trim() !== ''
        const isInvalidProject = hasValue && !/^[0-9a-fA-F]{24}$/.test(finalProjectId)
        const isEmpty = !projectId || projectId.toString().trim() === ''

        const isFormInvalid = !form.checkValidity()

        if (isFormInvalid || isInvalidProject || isEmpty) {
            setFormValid(true)
            if (isEmpty) {
                setProjectErrorKey('incomesProjectInvalid') 
            } else if (isInvalidProject) {
                setProjectErrorKey('incomesProjectNotFound')
            }
            return
        }

        const income = {
            projectId: finalProjectId,
            concept,
            amount,
            estimatedDate,
            date
        }

        try {
            if (elementId && action === "edit") {
                await IncomesService.updateIncome(income, elementId)
            } else if (action === "add" || action === "duplicate") {
                await IncomesService.addIncome(income)
            } else if (action === "delete") {
                await IncomesService.deleteIncome(elementId)
            }

            setFormValid(false)
            onSubmit()
        } catch (error) {
            const msg = error.response?.data?.error?.message || ""
            if (msg.includes("Cast to ObjectId failed")) {
                setProjectErrorKey('incomesProjectNotFound')
                setFormValid(true)
            } else {
                setShowMsg({ text: msg || "Error", style: 'danger' })
                setShowAlert(true)
            }
        } 
    }

    if (error) {
        return (
            <AlertError error={error} />
        )
    } else if (elementId && !isLoaded) {
        return (
            <AlertLoading isLoaded={isLoaded} />
        )
    }

    return (
        <div>
            <Form noValidate validated={formValid} onSubmit={handleSubmit}>
                {elementId && (
                    <Form.Group className="mb-3 mt-3" controlId="id">
                        <Form.Label className='fw-semibold'>Id:</Form.Label>
                        <Form.Control
                            type="text"
                            name="id"
                            value={elementId}
                            disabled={"disabled"}
                        />
                    </Form.Group>
                )}

                <Form.Group className="mb-4" controlId="projectId">
                    <Diagram3 /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="incomesProject" defaultMessage="Project" />:</Form.Label>

                    <SearchSelect
                        fieldName={'projectId'}
                        selected={projectId}
                        onChange={handleInputChange}
                        options={projectsList}
                        disabled={projectsList.length === 0}
                        className={projectErrorKey !== '' ? 'is-invalid border-danger' : (formValid && projectId ? 'is-valid' : '')}
                        style={projectErrorKey !== '' ? { backgroundImage: 'none' } : {}}
                        placeholder={projectsList.length === 0 ? intl.formatMessage({ id: "incomesProjectsNotFound", defaultMessage: "No projects available" }) : intl.formatMessage({ id: "textClickSelect", defaultMessage: "Click to select or search" })}
                    />

                    {projectErrorKey !== '' && (
                        <div className="text-danger small mt-1" style={{ display: 'block' }}>
                            <FormattedMessage id={projectErrorKey} />
                        </div>
                    )}
                </Form.Group>

                <Form.Group className="mb-4" controlId="concept">
                    <Receipt /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="incomesConcept" defaultMessage="Concept" />:</Form.Label>
                    <Form.Control
                        type="text"
                        name="concept"
                        value={concept}
                        onChange={handleInputChange}
                        disabled={inputDisabled && ("disabled")}
                        required="required"
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="incomesConceptInvalid" defaultMessage="Provide a concept" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-4" controlId="amount">
                    <Coin /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="incomesAmount" defaultMessage="Amount" />:</Form.Label>
                    <InputNumber
                        fieldName={"amount"}
                        value={amount}
                        onChange={handleInputChange}
                        disabled={inputDisabled && ("disabled")}
                        required="required"
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="incomesAmountInvalid" defaultMessage="Provide an amount" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Row className="mt-4 mb-4">
                    {/* Invoice Date */}
                    <Col md={6}>
                        <Form.Group className="mb-4" controlId="estimatedDate">
                            <Calendar3 /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="incomesEstimatedDate" defaultMessage="Estimated date" />:</Form.Label>
                            <InputDate
                                fieldName='estimatedDate'
                                value={estimatedDate}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="incomesEstimatedDateInvalid" defaultMessage="Provide an estimated income date" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-4" controlId="date">
                            <Calendar3 /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="incomesDate" defaultMessage="Income date" />:</Form.Label>
                            <InputDate
                                fieldName='date'
                                value={date}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="incomesDateInvalid" defaultMessage="Provide an income date" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>
                <AlertMsg message={showMsg.text} variant={showMsg.style} dismissible onCloseHandler={() => setShowAlert(false)} show={showAlert} />

                <div className="d-grid gap-2">
                    {action === "add" && (
                        <Button
                            className="btn btn-dark fw-semibold"
                            type="submit"
                        >
                            <PlusCircle /> {buttonTitle}
                        </Button>
                    )}

                    {action === "edit" && (
                        <Button
                            className="btn btn-primary fw-semibold"
                            type="submit"
                        >
                            <Save2 /> {buttonTitle}
                        </Button>
                    )}

                    {action === "delete" && (
                        <Button
                            className="btn btn-danger fw-semibold"
                            type="submit"
                        >
                            <Trash /> {buttonTitle}
                        </Button>
                    )}

                    {action === "duplicate" && (
                        <Button className="btn btn-dark fw-semibold" type="submit">
                            <Copy /> {buttonTitle}
                        </Button>
                    )}
                </div>
            </Form>
        </div>
    )

}

export default IncomesForm